import { useState, useEffect } from 'react';
import { violationsAPI, objectsAPI, usersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime, getMonthStartDate, getMonthEndDate } from '@/lib/utils';
import { 
  AlertTriangle, 
  Filter, 
  Loader2,
  Search,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function Violations() {
  const { isEmployee, isAdmin, isHR } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [violations, setViolations] = useState([]);
  const [objects, setObjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    user_id: 'all-employees',
    object_id: 'all-objects',
    start_date: getMonthStartDate(),
    end_date: getMonthEndDate()
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      if (isEmployee) {
        const res = await violationsAPI.my({
          start_date: filters.start_date,
          end_date: filters.end_date
        });
        setViolations(res.data);
      } else {
        const params = {
          start_date: filters.start_date,
          end_date: filters.end_date,
        };
        if (filters.user_id && filters.user_id !== 'all-employees') {
          params.user_id = filters.user_id;
        }
        if (filters.object_id && filters.object_id !== 'all-objects') {
          params.object_id = filters.object_id;
        }
        
        const [violationsRes, objectsRes, usersRes] = await Promise.all([
          violationsAPI.list(params),
          objectsAPI.list(),
          usersAPI.list()
        ]);
        setViolations(violationsRes.data);
        setObjects(objectsRes.data);
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Failed to load violations:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    await loadData();
  };

  const getViolationTypeLabel = (type) => {
    const key = `violations.type.${type}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const employeeUsers = users.filter((user) => user.role === 'employee');

  const canManage = isAdmin || isHR;

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await violationsAPI.delete(id);
      setViolations(prev => prev.filter(v => v.id !== id));
      toast.success(t('msg.deleted'));
    } catch {
      toast.error(t('msg.failedDelete'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="violations-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-500/20 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-rose-400" />
          </div>
          {t('violations.title')}
        </h1>
        <p className="text-wp-light/40 mt-2">
          {isEmployee ? t('violations.yourHistory') : t('violations.subtitle')}
        </p>
      </div>

      {/* Filters */}
      {!isEmployee && (
        <div className="glass-card rounded-2xl p-4 md:p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <Filter className="h-5 w-5 text-wp-light" />
            </div>
            <h2 className="text-lg font-semibold text-white font-heading">{t('reports.filters')}</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('reports.startDate')}</Label>
              <Input
                type="date"
                lang={language}
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                className="bg-wp-dark/30 border-wp-dark/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('reports.endDate')}</Label>
              <Input
                type="date"
                lang={language}
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                className="bg-wp-dark/30 border-wp-dark/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('common.employee')}</Label>
              <Select value={filters.user_id} onValueChange={(v) => setFilters({...filters, user_id: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                  <SelectValue placeholder={t('common.allEmployees')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-employees">{t('common.allEmployees')}</SelectItem>
                  {employeeUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-wp-light/70">{t('common.object')}</Label>
              <Select value={filters.object_id} onValueChange={(v) => setFilters({...filters, object_id: v})}>
                <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                  <SelectValue placeholder={t('common.allObjects')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-objects">{t('common.allObjects')}</SelectItem>
                  {objects.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <button 
                onClick={applyFilters} 
                disabled={loading}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {t('common.search')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="glass-card rounded-2xl p-6 border-l-4 border-rose-500">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-rose-500/20 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-rose-400" />
          </div>
          <div>
            <p className="text-4xl font-bold text-rose-400 font-heading">{violations.length}</p>
            <p className="text-wp-light/40">{t('violations.totalViolations')}</p>
          </div>
        </div>
      </div>

      {/* Violations Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-wp-dark/50 hover:bg-transparent">
                {!isEmployee && <TableHead className="text-wp-light/50">{t('common.employee')}</TableHead>}
                <TableHead className="text-wp-light/50">{t('common.object')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.type')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.description')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.date')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.time')}</TableHead>
                {canManage && <TableHead className="text-wp-light/50 text-right">{t('common.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {violations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isEmployee ? 5 : (canManage ? 7 : 6)} className="text-center py-12 text-wp-light/40">
                    {t('violations.noViolations')}
                  </TableCell>
                </TableRow>
              ) : (
                violations.map((v) => (
                  <TableRow key={v.id} className="border-wp-dark/30 hover:bg-wp-dark/30" data-testid={`violation-row-${v.id}`}>
                    {!isEmployee && <TableCell className="font-medium text-white">{v.user_name}</TableCell>}
                    <TableCell className="text-wp-light/70">{v.object_name || '-'}</TableCell>
                    <TableCell>
                      <span className="px-2.5 py-1 text-xs rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/20">
                        {getViolationTypeLabel(v.violation_type)}
                      </span>
                    </TableCell>
                    <TableCell className="text-wp-light/50">{v.description}</TableCell>
                    <TableCell className="text-wp-light/70">{v.date}</TableCell>
                    <TableCell className="text-wp-light/50">
                      {formatDateTime(v.created_at).split(',')[1]}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <button
                          onClick={() => handleDelete(v.id)}
                          className="p-1.5 rounded-lg text-wp-light/40 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
